import { Link } from "react-router-dom";
import type { Work } from "@/types/content";
import { formatDateCn, getRelatedNews, getRelatedPlots, getSimilarWorks } from "@/lib/content";
import WorkCard from "@/components/base/WorkCard";
import ShareButton from "./ShareButton";

interface WorkDetailProps {
  work: Work;
}

export default function WorkDetail({ work }: WorkDetailProps) {
  const relatedNews = getRelatedNews(work.id);
  const relatedPlots = getRelatedPlots(work.id);
  const similarWorks = getSimilarWorks(work.id, 4);

  const metaList = [
    { label: "导演", value: work.director },
    { label: "主演", value: work.cast.join(" / ") },
    { label: "类型", value: work.genre.join(" / ") },
    { label: "年份", value: `${work.year}` },
    { label: "地区", value: work.region },
    { label: "首播", value: work.firstAir },
  ];

  return (
    <div className="hp-fade-up">
      <section className="relative w-full">
        <div className="absolute inset-0 h-[420px] md:h-[480px] overflow-hidden">
          <img
            src={work.backdrop}
            alt={work.title}
            title={`${work.title} 剧照`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-foreground-950/80 to-foreground-950/70" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-6 pt-28 md:pt-32">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 sm:w-48 shrink-0 mx-auto md:mx-0">
              <div className="w-full aspect-[3/4] rounded-lg overflow-hidden border border-background-50/20 bg-background-200">
                <img
                  src={work.cover}
                  alt={work.title}
                  title={`${work.title} 影视作品海报`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0 text-background-50">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="bg-accent-500 text-background-50 text-xs px-2.5 py-1 rounded-md flex items-center gap-1 font-label font-semibold">
                  <i className="ri-star-fill" />
                  {work.rating.toFixed(1)}
                </span>
                <span className="bg-background-50/15 text-background-50 text-xs px-2.5 py-1 rounded-md">
                  {work.type}
                </span>
                {work.tags.map((tag) => (
                  <span key={tag} className="bg-background-50/15 text-background-100 text-xs px-2.5 py-1 rounded-md">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tight mb-4">
                {work.title}
              </h1>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 max-w-2xl mb-6 text-sm">
                {metaList.map((m) => (
                  <div key={m.label} className="flex gap-2">
                    <span className="text-background-300 shrink-0 w-10">{m.label}</span>
                    <span className="text-background-100">{m.value}</span>
                  </div>
                ))}
              </div>
              <ShareButton />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 pt-10 md:pt-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2 space-y-10">
            <div>
              <h2 className="font-heading font-black text-2xl text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1.5 h-6 rounded-full bg-primary-500" />
                作品简介
              </h2>
              <p className="text-sm md:text-[15px] text-foreground-700 leading-loose">
                {work.synopsis}
              </p>
            </div>

            <div>
              <h2 className="font-heading font-black text-2xl text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1.5 h-6 rounded-full bg-accent-500" />
                相关资讯
              </h2>
              {relatedNews.length > 0 ? (
                <div className="space-y-3">
                  {relatedNews.map((item) => (
                    <Link
                      key={item.id}
                      to={`/detail/${item.id}`}
                      className="group flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4 hover:border-primary-300 transition-colors"
                    >
                      <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                        <i className="ri-newspaper-line" />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-medium text-sm text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug">
                          {item.title}
                        </span>
                        <span className="block text-xs text-foreground-500 mt-1">
                          {formatDateCn(item.date)} · {item.source}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-foreground-500 rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-6 text-center">
                  暂无与《{work.title}》相关的资讯内容。
                </p>
              )}
            </div>
          </div>

          <aside className="space-y-8">
            <div>
              <h2 className="font-heading font-black text-xl text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-movie-2-line text-primary-500" />
                同类推荐
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {similarWorks.map((w) => (
                  <WorkCard key={w.id} work={w} />
                ))}
              </div>
            </div>

            {relatedPlots.length > 0 && (
              <div>
                <h2 className="font-heading font-black text-xl text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-lightbulb-line text-accent-500" />
                  相关解析
                </h2>
                <div className="space-y-2">
                  {relatedPlots.map((p) => (
                    <Link
                      key={p.id}
                      to={`/detail/${p.id}`}
                      className="group flex items-center gap-2 rounded-md bg-secondary-50 border border-secondary-200 px-3.5 py-3 hover:border-primary-300 transition-colors"
                    >
                      <i className="ri-article-line text-secondary-500" />
                      <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {p.title}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
}